import { BigDecimal } from "@solomon-labs/bigdecimal";
import type { PublicKey } from "@solomon-labs/publickey";

import { useWalletStore } from "../../stores/walletStore";
import type { BalanceOverrideState, TokenBalanceDelta, TokenBalanceKey, TokenBalances } from "../types/balances";

export const DEFAULT_STALE_BALANCE_MS = 20_000;
export const TOKEN_BALANCE_KEYS: TokenBalanceKey[] = ["usdv", "legacyUsdv", "usdc", "usdt", "susdv", "solo"];

/** Delay between refetch attempts during reconciliation */
const REFETCH_DELAY_MS = 2_000;
/** Maximum number of refetch attempts during reconciliation */
const REFETCH_MAX_ATTEMPTS = 2;
/** Default timeout for the entire reconciliation process */
const DEFAULT_RECONCILIATION_TIMEOUT_MS = 15_000;
/** Delays for background recovery retries after initial reconciliation exhausts attempts */
const BACKGROUND_RETRY_DELAYS_MS = [5_000, 10_000, 20_000];

export interface ReconcileBalancesOptions {
    timeoutMs?: number;
}

/** Cleanup function for any in-progress background recovery */
let backgroundRecoveryCleanup: (() => void) | null = null;

/** Delay helper */
export const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

export function bigDecimalEquals(a: BigDecimal | null, b: BigDecimal | null): boolean {
    if (a === null && b === null) return true;
    if (a === null || b === null) return false;
    return a.eq(b);
}

/**
 * Check if on-chain balances have caught up to a mutation by comparing fetched
 * values against the pre-mutation snapshot.
 *
 * Returns true only if ALL impacted keys (where expected differs from pre-swap)
 * have fetched values different from their pre-swap values.
 *
 * Why "all differ from preSwap" rather than "all match expected":
 * - "All match expected" fails for swaps due to slippage (actual != quoted).
 * - "All differ from preSwap" is slippage-resistant because any landed
 *   transaction will move its impacted keys away from their prior values.
 *
 * Why "all" rather than "any":
 * - Requiring ALL impacted keys to have moved prevents premature clearing
 *   when an earlier overlapping mutation settles with slippage, shifting
 *   some (but not all) keys away from their pre-swap values.
 * - Solana transactions are atomic, so if the current mutation landed,
 *   all its impacted accounts updated in the same slot.
 */
export function hasChainCaughtUp(override: BalanceOverrideState, fetchedBalances: TokenBalances): boolean {
    let hasImpactedKey = false;

    for (const key of TOKEN_BALANCE_KEYS) {
        // Skip non-impacted keys (expected == pre-swap)
        if (bigDecimalEquals(override.expectedBalances[key], override.preSwapBalances[key])) continue;

        hasImpactedKey = true;

        // If any impacted key still matches pre-swap, chain hasn't fully caught up
        if (bigDecimalEquals(fetchedBalances[key], override.preSwapBalances[key])) return false;
    }

    return hasImpactedKey;
}

/**
 * Atomically set fetched balances and clear the override if the chain has caught up.
 * Single set() call prevents intermediate renders where balances are updated but override isn't.
 */
export function setFetchedBalances(
    set: (
        fn: (state: {
            balanceOverride: BalanceOverrideState | null;
            mutationVersion: number;
            lastFetchedAddress: string | null;
        }) => Record<string, unknown>
    ) => void,
    fetchedBalances: TokenBalances,
    ownerAddress: string
): boolean {
    let overrideCleared = false;

    set((state) => {
        const override = state.balanceOverride;
        const shouldClear =
            override !== null &&
            state.mutationVersion === override.version &&
            hasChainCaughtUp(override, fetchedBalances);

        if (shouldClear) overrideCleared = true;

        return {
            balances: fetchedBalances,
            isLoadingBalances: false,
            isFetchingBalances: false,
            lastFetchedAt: Date.now(),
            lastFetchedAddress: ownerAddress,
            abortController: null,
            balanceOverride: shouldClear ? null : state.balanceOverride
        };
    });

    return overrideCleared;
}

/**
 * Schedule bounded background retries after initial reconciliation exhausts its attempts.
 * Each retry checks if the override is still relevant before fetching.
 */
function scheduleBackgroundRecovery(
    version: number,
    publicKey: PublicKey,
    fetchBalances: (owner: PublicKey, force?: boolean) => Promise<void>
): void {
    const pendingTimeouts: ReturnType<typeof setTimeout>[] = [];
    let cancelled = false;

    backgroundRecoveryCleanup = (): void => {
        cancelled = true;
        for (const id of pendingTimeouts) clearTimeout(id);
        pendingTimeouts.length = 0;
    };

    let cumulativeDelay = 0;
    for (const delayMs of BACKGROUND_RETRY_DELAYS_MS) {
        cumulativeDelay += delayMs;
        const id = setTimeout(async () => {
            if (cancelled) return;

            const state = useWalletStore.getState();
            // Bail if superseded by a newer mutation or override already cleared
            if (state.mutationVersion !== version) return;
            if (state.balanceOverride?.version !== version) return;

            await fetchBalances(publicKey, true);
        }, cumulativeDelay);
        pendingTimeouts.push(id);
    }
}

export function applyDeltaToBalances(balances: TokenBalances, delta: TokenBalanceDelta): TokenBalances {
    const nextBalances: TokenBalances = { ...balances };

    for (const key of TOKEN_BALANCE_KEYS) {
        const deltaValue = delta[key];
        if (!deltaValue) {
            continue;
        }

        const baseValue = nextBalances[key] ?? new BigDecimal(0);
        nextBalances[key] = baseValue.add(deltaValue);
    }

    return nextBalances;
}

export function isBalanceDataStale(
    lastFetchedAt: number | null,
    lastMutationAt: number | null,
    maxAgeMs: number
): boolean {
    if (lastFetchedAt === null) {
        return true;
    }

    if (lastMutationAt !== null && lastFetchedAt < lastMutationAt) {
        return true;
    }

    return Date.now() - lastFetchedAt > maxAgeMs;
}

/**
 * Compute the display balances by overlaying the override on the stored balances.
 * Only impacted keys (where expected differs from pre-swap) use the override value;
 * non-impacted keys always show the latest fetched balance.
 */
export function getDisplayBalances(
    storedBalances: TokenBalances,
    override: { expectedBalances: TokenBalances; preSwapBalances: TokenBalances } | null
): TokenBalances {
    if (!override) return storedBalances;

    const result: TokenBalances = { ...storedBalances };

    for (const key of TOKEN_BALANCE_KEYS) {
        const expected = override.expectedBalances[key];
        const preSwap = override.preSwapBalances[key];

        // Only override impacted keys (where expected differs from pre-swap)
        const bothNull = expected === null && preSwap === null;
        const eitherNull = expected === null || preSwap === null;
        const isImpacted = bothNull ? false : eitherNull ? true : !expected.eq(preSwap);

        if (isImpacted) {
            result[key] = expected;
        }
    }

    return result;
}

/**
 * After a swap, set an optimistic balance override and refetch until the
 * on-chain state catches up. The store's fetchBalances atomically clears
 * the override when it detects the chain has updated.
 */
export async function reconcileBalancesAfterSwap(
    publicKey: PublicKey,
    fetchBalances: (owner: PublicKey, force?: boolean) => Promise<void>,
    delta: TokenBalanceDelta,
    options: ReconcileBalancesOptions = {}
): Promise<void> {
    const hasImpactedKeys = TOKEN_BALANCE_KEYS.some((key) => !!delta[key]);
    if (!hasImpactedKeys) {
        // No balance change expected — just refetch
        await fetchBalances(publicKey, true);
        return;
    }

    // Cancel any previous background recovery
    backgroundRecoveryCleanup?.();
    backgroundRecoveryCleanup = null;

    const store = useWalletStore.getState();
    const currentDisplayed = getDisplayBalances(store.balances, store.balanceOverride);
    const expectedBalances = applyDeltaToBalances(currentDisplayed, delta);
    const preSwapBalances = currentDisplayed;

    const version = store.setBalanceOverride(expectedBalances, preSwapBalances);
    store.setReconcilingBalances(true);

    const timeoutMs = options.timeoutMs ?? DEFAULT_RECONCILIATION_TIMEOUT_MS;
    const deadline = Date.now() + timeoutMs;

    try {
        for (let attempt = 0; attempt < REFETCH_MAX_ATTEMPTS; attempt++) {
            if (attempt > 0) {
                if (Date.now() + REFETCH_DELAY_MS > deadline) break;
                await delay(REFETCH_DELAY_MS);
            }

            // Check if a newer swap superseded us
            if (useWalletStore.getState().mutationVersion !== version) return;

            await fetchBalances(publicKey, true);

            // fetchBalances atomically clears the override if chain caught up
            const latest = useWalletStore.getState();
            if (latest.balanceOverride?.version !== version) {
                return; // Override was cleared — reconciliation succeeded
            }
        }
    } finally {
        const finalState = useWalletStore.getState();
        if (finalState.mutationVersion === version) {
            finalState.setReconcilingBalances(false);

            // If override still present after all attempts, mark stale and
            // schedule background recovery so users who stay on one page recover
            if (finalState.balanceOverride?.version === version) {
                finalState.markBalancesStale();
                scheduleBackgroundRecovery(version, publicKey, fetchBalances);
            }
        }
    }
}
